<template>
  <b-card class="p-2">
    <b-row class="p-0 collection-edit">
      <b-col class="col-12 col-md-7 col-lg-6">
        <b-row>
          <b-col class="col-12 col-sm-6">
            <template>
              <div
                id="intro_collection_logo"
                class="image rounded object-contain"
                :class="{ 'preview-image-border': !logoPreview }"
              >
                <label for="collection_logo">
                  <b-img
                    v-if="logoPreview"
                    class="collectionLogo bg-gray img img-responsive fullwidth"
                    :src="logoPreview"
                    alt=""
                  />
                  <div
                    v-if="!logoPreview"
                    class="logo-preview"
                  >
                    <camera-icon />
                    <span
                      class="text-center font-weight-700 color-text-black"
                    >Upload collection <br>
                      cover photo
                    </span>
                  </div>
                </label>
              </div>
            </template>
          </b-col>
          <b-col class="mt-1 col-12 col-sm-6 mt-sm-0">
            <div class="mass-products-label mb-1">
              Mass Upload Products
            </div>
            <div
              id="intro_sample_product_sheet"
              class="mt-8-px"
            >
              <b-button
                variant="custom-secondary"
                block
                @click="onDownloadSampleSheetClick"
              >
                {{ isEdit ? 'Download Collection' : 'Sample Product Sheet' }}
              </b-button>
            </div>
            <div
              v-if="!isDromoUpload"
              id="intro_collection_products"
              class="w-100"
            >
              <label
                class="w-100 rounded text-center preview-image-border upload-xlsx"
                :class="{ 'has-file': productFile }"
                for="collection_products"
              >
                <div
                  v-if="productFile && productFile.name"
                  class="elements-center w-100 color-text-black font-weight-700"
                >
                  <div>{{ textTruncate(productFile.name) }}</div>
                  <div
                    class="ml-8-px"
                    @click.prevent="clearFile"
                  >
                    <collection-file-delete-icon />
                  </div>
                </div>
                <div
                  v-else
                  class="elements-center w-100 color-text-black font-weight-700"
                >
                  <div>Upload file</div>
                  <mass-upload-icon class="ml-8-px" />
                </div>
              </label>
              <b-form-file
                v-if="!productFile"
                id="collection_products"
                v-model="productFile"
                accept=".xlsx, .zip"
                placeholder="Choose a file or drop it here..."
                drop-placeholder="Drop file here..."
                plain
                hidden="true"
                @input="handleFileUpload"
              />
            </div>
            <b-row class="mt-2 w-100 no-gutters">
              <b-col>
                <b-button
                  v-if="enableDromoDemo && !isFileUpload"
                  class="w-100"
                  variant="primary"
                  @click="startDromo"
                >
                  <span>Upload products with AI</span>
                  <span> (xlsx) </span>
                  <ai-stars-svg class="ai-star-icon" />
                </b-button>
                <p
                  v-if="isDromoUploadDone"
                  class="upload-success-message"
                >
                  File Uploaded successfully
                </p>
              </b-col>
            </b-row>
            <!-- KP-3919 added the catalogue-file controls; this page had none
                 before, so with the flag off they are simply absent. -->
            <template v-if="isBrandCatalogueEnabled">
              <!-- Brand Catalogue (KP-3919, multiple files per section) -->
              <div class="mass-products-label mt-2 mb-1">
                Brand Catalogue (optional)
              </div>
              <div
                v-for="(file, index) in collection.brandCatalogFiles"
                :key="`catalog-${index}-${file.url}`"
                class="w-100 rounded preview-image-border upload-xlsx has-file mb-50 brand-catalog-row"
              >
                <div class="elements-center w-100 color-text-black font-weight-700">
                  <div>{{ textTruncate(file.name) }}</div>
                  <div
                    class="ml-8-px"
                    @click.prevent="removeBrandCatalogFile(index)"
                  >
                    <collection-file-delete-icon />
                  </div>
                </div>
              </div>
              <brand-catalog-upload-row
                v-if="brandCatalogUpload.section === 'catalog'"
                :upload="brandCatalogUpload"
                @pause="pauseBrandCatalogUpload"
                @resume="resumeBrandCatalogUpload"
                @cancel="cancelBrandCatalogUpload"
              />
              <label
                class="w-100 rounded text-center preview-image-border upload-xlsx"
                for="brand_catalog_pdf"
              >
                <div class="elements-center w-100 color-text-black font-weight-700">
                  <div>Add catalogue files (.pdf, .ppt, .pptx, .html)</div>
                  <mass-upload-icon class="ml-8-px" />
                </div>
              </label>
              <b-form-file
                id="brand_catalog_pdf"
                accept=".pdf,.ppt,.pptx,.html"
                placeholder="Choose files or drop them here..."
                drop-placeholder="Drop files here..."
                plain
                hidden="true"
                multiple
                @input="onBrandCatalogFilesPicked"
              />
              <div
                v-for="(file, index) in collection.brandCatalogAddonFiles"
                :key="`addon-${index}-${file.url}`"
                class="w-100 rounded preview-image-border upload-xlsx has-file mt-1 mb-50 brand-catalog-row"
              >
                <div class="elements-center w-100 color-text-black font-weight-700">
                  <div>{{ textTruncate(file.name) }}</div>
                  <div
                    class="ml-8-px"
                    @click.prevent="removeBrandCatalogAddonFile(index)"
                  >
                    <collection-file-delete-icon />
                  </div>
                </div>
              </div>
              <brand-catalog-upload-row
                v-if="brandCatalogUpload.section === 'addon'"
                :upload="brandCatalogUpload"
                @pause="pauseBrandCatalogUpload"
                @resume="resumeBrandCatalogUpload"
                @cancel="cancelBrandCatalogUpload"
              />
              <label
                class="w-100 rounded text-center preview-image-border upload-xlsx mt-1"
                for="brand_catalog_addon"
              >
                <div class="elements-center w-100 color-text-black font-weight-700">
                  <div>Add additional files (.ppt, .xls, .pdf, .csv, .zip, .html)</div>
                  <mass-upload-icon class="ml-8-px" />
                </div>
              </label>
              <b-form-file
                id="brand_catalog_addon"
                accept=".ppt,.pptx,.xls,.xlsx,.pdf,.csv,.zip,.html"
                placeholder="Choose files or drop them here..."
                drop-placeholder="Drop files here..."
                plain
                hidden="true"
                multiple
                @input="onBrandCatalogAddonFilesPicked"
              />
            </template>
          </b-col>
        </b-row>
      </b-col>
      <b-col class="col-12 col-md-5 col-lg-6 mt-1 mt-lg-0">
        <validation-observer
          ref="collectionForm"
          #default="{ invalid }"
        >
          <b-form @submit.prevent="onSyncCollection">
            <!-- name -->
            <b-form-group
              label="Collection Name*"
              label-for="intro_collection_name"
            >
              <validation-provider
                #default="{ errors }"
                name="Collection Name"
                vid="name"
                rules="required"
              >
                <b-form-input
                  id="intro_collection_name"
                  v-model="collection.name"
                  name="name"
                  placeholder="Collection Name"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <!-- description -->
            <b-form-group
              label="Description*"
              label-for="intro_collection_description"
            >
              <validation-provider
                #default="{ errors }"
                name="Collection Description"
                vid="description"
                rules="required|min:3"
              >
                <b-form-input
                  id="intro_collection_description"
                  v-model="collection.description"
                  placeholder="Enter Collection Description..."
                  :state="errors.length > 0 ? false : null"
                  required
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <b-form-group
              label="Images in ZIP"
              class="hidden"
            >
              <b-form-textarea
                v-if="enableDromoDemo"
                v-model="filesInZip"
                max-rows="4"
              />
            </b-form-group>
            <!-- end date -->
            <div>
              <label> Collection's Products Availability*</label>
              <br>
              <b-form-radio-group
                id="file-radio-group"
                v-model="collection.type"
                name="file-radio-group"
                class="file-radio-btn"
              >
                <b-form-radio
                  v-for="collectionTypeOption in Object.values(
                    COLLECTION_TYPE
                  ).map((_) => _)"
                  :key="collectionTypeOption.TEXT"
                  :value="collectionTypeOption.LABEL"
                >
                  {{ collectionTypeOption.TEXT }}
                </b-form-radio>
              </b-form-radio-group>
            </div>
            <b-row>
              <b-col class="col-6 pr-50">
                <b-form-group
                  class="w-100"
                  label="Publish Date*"
                  label-for="intro_collection_publish_date"
                >
                  <flat-pickr
                    id="intro_collection_publish_date"
                    ref="startDate"
                    v-model="collection.startDateString"
                    class="form-control"
                    :config="{ dateFormat: KP_DATE_FORMAT.CONFIG }"
                    :placeholder="
                      collection.startDateString || KP_DATE_FORMAT.PLACEHOLDER
                    "
                  />
                </b-form-group>
              </b-col>
              <b-col class="col-6 pl-50">
                <b-form-group
                  class="w-100"
                  label="Order Deadline"
                  label-for="intro_collection_end_date"
                >
                  <flat-pickr
                    id="intro_collection_end_date"
                    ref="endDate"
                    v-model="collection.endDateString"
                    class="form-control"
                    :config="{ dateFormat: KP_DATE_FORMAT.CONFIG }"
                    :placeholder="
                      collection.endDateString || KP_DATE_FORMAT.PLACEHOLDER
                    "
                  />
                  <small
                    v-if="expireDateError"
                    class="text-danger"
                  >The Order Deadline is required!</small>
                </b-form-group>
              </b-col>
            </b-row>
            <b-form-group
              id="intro_collection_availability"
              class="w-100"
            >
              <template #label>
                <span class="font-small-4">Estimated Delivery Window</span>
              </template>
              <b-row>
                <b-col class="col-6 pr-50">
                  <div class="position-relative">
                    <flat-pickr
                      ref="fromDate"
                      v-model="fromDate"
                      class="form-control"
                      :config="VUE_FLATPICKR_MONTH_CONFIG"
                      :placeholder="fromDate || KP_MONTH_FORMAT.PLACEHOLDER"
                    />
                    <feather-icon
                      icon="CalendarIcon"
                      size="20"
                      class="calendar-icon-delivery-window"
                    />
                  </div>
                </b-col>
                <b-col class="col-6 pl-50">
                  <div class="position-relative">
                    <flat-pickr
                      ref="toDate"
                      v-model="toDate"
                      class="form-control"
                      :config="VUE_FLATPICKR_MONTH_CONFIG"
                      :placeholder="toDate || KP_MONTH_FORMAT.PLACEHOLDER"
                    />
                    <feather-icon
                      icon="CalendarIcon"
                      size="20"
                      class="calendar-icon-delivery-window"
                    />
                  </div>
                </b-col>
              </b-row>
            </b-form-group>

            <!-- logo -->
            <b-form-file
              id="collection_logo"
              v-model="img"
              accept=".jpg,.png,.jpeg,.svg"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
              plain
              hidden="true"
            />
            <!-- action -->
            <b-button
              type="submit"
              variant="custom-primary"
              class="font-weight-bold font-size-16-px mt-1"
              :disabled="invalid || disabled"
            >
              {{ buttonText }}
            </b-button>
          </b-form>
        </validation-observer>
      </b-col>
      <b-modal
        id="cropcoverphoto"
        ref="cropcoverphoto"
        hide-header
        hide-footer
        no-close-on-backdrop
        no-close-on-esc
        centered
        size="lg"
      >
        <div class="pb-1 mb-2 border-bottom">
          <span class="custom-modal-heading">Crop cover photo </span>
          <feather-icon
            class="float-right close-x-icon"
            icon="XIcon"
            size="24"
            @click="closeCrop"
          />
        </div>
        <div class="cropper-section mb-1">
          <cropper
            ref="cropper"
            class="cropper"
            :src="croppedImg"
            :stencil-props="{
              aspectRatio: 1 / 1,
            }"
            image-restriction="fit-area"
          />
          <div class="text-center mt-1">
            <b-button
              class="mr-1 font-weight-bolder"
              variant="outline-info"
              @click="closeCrop"
            >
              Cancel
            </b-button>
            <b-button
              class="ml-1"
              variant="info"
              @click="crop"
            >
              {{ isCropping ? 'Cropping....' : 'Crop' }}
            </b-button>
          </div>
        </div>
      </b-modal>
      <!-- Confirm Update Collection -->
      <b-modal
        id="confirm_orders_update_modal"
        size="lg"
        modal-class="modal-primary custom-model"
        centered
        hide-header-close
        hide-header
        hide-footer
      >
        <div>
          <div class="pb-1 border-bottom">
            <span
              class="custom-modal-heading"
            >Collection Update Disclaimer!</span>
            <feather-icon
              class="float-right close-x-icon"
              icon="XIcon"
              size="24"
              @click="$bvModal.hide('confirm_orders_update_modal')"
            />
          </div>
          <p class="mt-1 text-center product-message">
            Some SKUs with updated prices exist in orders from retailers. Would
            you like to update prices in not confirmed orders?
          </p>
        </div>
        <div>
          <div class="text-center my-1">
            <b-button
              class="mt-1 mr-2 px-sm-3 font-weight-bolder"
              variant="outline-info"
              @click="$bvModal.hide('confirm_orders_update_modal')"
            >
              Cancel
            </b-button>
            <b-button
              class="mt-1 mr-2 px-sm-3 font-weight-bolder"
              variant="info"
              @click="onDoNotUpdateOrders"
            >
              Do not update orders
            </b-button>
            <b-button
              variant="info"
              class="mt-1 px-sm-3 font-weight-bolder"
              @click="openOrdersPopop"
            >
              Update orders
            </b-button>
          </div>
        </div>
      </b-modal>
      <collection-edit-modals
        :orders="ordersToUpdate"
        :update-orders="onUpdateOrders"
      />
      <dowload-catalog
        v-if="collectionId"
        :collection="collection"
      />
      <tool-kit :toolkit-page="TOOLKIT_PAGE.COLLECTION_EDIT" />
    </b-row></b-card>
</template>

<script>
import CollectionFileDeleteIcon from '@/@core/assets/svg-components/CollectionFileDeleteIcon.vue'
import MassUploadIcon from '@/@core/assets/svg-components/MassUploadIcon.vue'
import AiStarsSvg from '@/@core/assets/svg-components/AIStarsSvg.vue'
import {
  TOOLKIT_PAGE,
  VUE_FLATPICKR_MONTH_CONFIG,
} from '@/constants'
import { required } from '@validations'
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormRadio,
  BFormRadioGroup,
  BImg,
  BModal,
  BRow,
  VBModal,
  VBTooltip,
} from 'bootstrap-vue'
import lodash from 'lodash'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { Cropper } from 'vue-advanced-cropper'
import { CameraIcon } from 'vue-feather-icons'
import flatPickr from 'vue-flatpickr-component'
import Ripple from 'vue-ripple-directive'
import BrandCatalogUploadRow from '@/components/collection/BrandCatalogUploadRow.vue'
import { useCollectionEdit } from '@/composables/useCollectionEdit'
import { useBrandCatalogueFlag } from '@/composables/useBrandCatalogueFlag'
import DowloadCatalog from './DowloadCatalog.vue'
import CollectionEditModals from './CollectionEditModals.vue'

export default {
  components: {
    BrandCatalogUploadRow,
    BCard,
    BRow,
    BForm,
    BFormGroup,
    BCol,
    BFormInput,
    BFormTextarea,
    BFormFile,
    BButton,
    BImg,
    BModal,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
    DowloadCatalog,
    Cropper,
    CameraIcon,
    BFormRadio,
    BFormRadioGroup,
    MassUploadIcon,
    CollectionFileDeleteIcon,
    CollectionEditModals,
    AiStarsSvg,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
    'v-b-modal': VBModal,
  },
  setup() {
    const composable = useCollectionEdit(null)
    return {
      ...composable,
      ...useBrandCatalogueFlag(),
    }
  },
  data() {
    return {
      expireDateError: null,
      xlsxIcon: require('@/assets/images/icons/xlsxIcon.svg'),
      zipIcon: require('@/assets/images/icons/zipIcon.svg'),
      required,
      TOOLKIT_PAGE,
      VUE_FLATPICKR_MONTH_CONFIG,
      lodash,
      filesInZip: 'image1.jpg\nimage2.jpg\nimage3.jpg',
    }
  },
  watch: {
    img: {
      handler(theFile) {
        if (!theFile) return
        this.$refs.cropcoverphoto.show()
      },
    },
  },
  created() {
    this.collectionId = this.$route.params.id || null
    if (this.collectionId) {
      this.fetchCollection(() => this.updateBreadcrumb())
    } else {
      this.updateBreadcrumb()
    }
  },
  methods: {
    progressCallback(data) {
      this.$root.$progressBar.value = data
    },
    onBrandCatalogFilesPicked(files) {
      const list = Array.isArray(files) ? files : [files]
      this.uploadBrandCatalogFiles(list.filter(Boolean))
    },
    onBrandCatalogAddonFilesPicked(files) {
      const list = Array.isArray(files) ? files : [files]
      this.uploadBrandCatalogAddonFiles(list.filter(Boolean))
    },
    onSyncCollection() {
      this.$refs.collectionForm
        .validate()
        .then(async success => {
          if (success) {
            await this.syncCollection(this.progressCallback, this.$router, {
              onSuccess: () => this.$bvModal.hide('confirm_orders_update_modal'),
              onOrderConflict: () => this.$bvModal.show('confirm_orders_update_modal'),
            })
          }
        })
    },
    onDoNotUpdateOrders() {
      this.doNotUpdateOrders()
      this.updateCollection(this.progressCallback, this.$router, {
        onSuccess: () => this.$bvModal.hide('confirm_orders_update_modal'),
        onOrderConflict: () => this.$bvModal.show('confirm_orders_update_modal'),
      })
    },
    openOrdersPopop() {
      this.$bvModal.hide('confirm_orders_update_modal')
      this.$bvModal.show('select_orders_modal')
    },
    onUpdateOrders(orderIds) {
      this.updateOrdersPayload(orderIds)
      this.updateCollection(this.progressCallback, this.$router, {
        onSuccess: () => this.$bvModal.hide('confirm_orders_update_modal'),
        onOrderConflict: () => this.$bvModal.show('confirm_orders_update_modal'),
      })
    },
    onDownloadSampleSheetClick() {
      this.onDownloadSampleSheet(id => {
        this.$bvModal.show(`download_catalog_${id}`)
      })
    },
    updateBreadcrumb() {
      this.$route.meta.breadcrumb = this.$route.meta.breadcrumb.map(item => {
        if (item.dynamic === true) {
          if (item.hasOwnProperty('actionName')) {
            item.actionName = this.collection.name
              ? `Edit <b>${this.collection.name}</b>`
              : 'Add Collection'
          }
        }
        return item
      })
    },
    toggleDatePicker() {
      this.$refs.endDate.$el._flatpickr.toggle()
    },
    toggleStartDatePicker() {
      this.$refs.startDate.$el._flatpickr.toggle()
    },
    closeCrop() {
      this.img = null
      this.$refs.cropcoverphoto.hide()
    },
    async crop() {
      await this.cropImage(this.$refs.cropper, () => {
        this.$refs.cropcoverphoto.hide()
      })
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/base/pages/app-collection.scss';
@import '@core/scss/vue/libs/vue-advanced-cropper.scss';

.ai-star-icon {
  width: 25px;
  margin-left: 1rem;
}
.upload-success-message {
  text-align: center;
  color: green;
  font-weight: bold;
  margin-top: 10px;
  padding: 5px;
  border: 1px solid green;
  border-radius: 5px;
  background-color: #e6ffe6;
}
.calendar-icon-delivery-window {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
</style>
